
import { useState, useEffect } from 'react';
import { User, UserFormData, Store } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { getStoresFromDatabase, getUsersByStoreDetailed } from '@/services/store';
import { useToast } from '@/components/ui/use-toast';

export function useUserManagement() {
  const { toast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');
  
  const [formData, setFormData] = useState<UserFormData>({
    name: "",
    email: "", 
    avatar: "",
    role: "user",
    points: 0,
    status: "active",
    connectedAccount: "",
  });

  useEffect(() => {
    fetchUsers();
    fetchStores();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [selectedStore, searchQuery, users]);

  const fetchStores = async () => {
    try {
      const storesData = await getStoresFromDatabase();
      setStores(storesData);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar as lojas.",
        variant: "destructive",
      });
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('admin-manage-users-simple', {
        body: { action: 'list', userData: { limit: 100 } }
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Não foi possível carregar os utilizadores');
      }

      const profiles = data.users || [];

      if (profiles.length === 0) {
        setUsers([]);
        setFilteredUsers([]);
        return;
      }

      // Mapear os dados para o formato User usando as colunas corretas
      const mappedUsers: User[] = profiles.map((profile: any) => ({
        id: profile.id,
        name: profile.username || profile.full_name || "",
        email: profile.email || "",
        avatar: profile.avatar_url || "",
        avatar_url: profile.avatar_url || "",
        role: profile.role || "user",
        points: profile.points || 0,
        createdAt: profile.created_at || new Date().toISOString(),
        status: (profile.status as 'active' | 'inactive') || "active",
        connectedAccountId: profile.connected_account_id || null,
        username: profile.username || profile.full_name || "",
        main_store_id: profile.main_store_id || undefined
      }));

      setUsers(mappedUsers);
      setFilteredUsers(mappedUsers);
    } catch (error: any) {
      // Log the full error for debugging (only in console)
      console.error('User fetch error:', error);

      // Provide user-friendly error messages without exposing backend details
      let userMessage = "Não foi possível carregar os utilizadores.";

      const errorMessage = error?.message || '';

      if (errorMessage.includes('Unauthorized') || errorMessage.includes('Admin role required')) {
        userMessage = "Não tem permissões de administrador para aceder aos utilizadores.";
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        userMessage = "Erro de conexão. Verifique a sua ligação à internet.";
      } else if (errorMessage.includes('timeout')) {
        userMessage = "A operação demorou muito tempo. Tente novamente.";
      }

      toast({
        title: "Erro",
        description: userMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = async () => {
    try {
      setLoading(true);
      let result = [...users];
      
      // Filtrar por loja, se seleccionada
      if (selectedStore) {
        try {
          const usersInStore = await getUsersByStoreDetailed(selectedStore);
          const userIds = usersInStore.map(user => user.id);
          result = result.filter(user => userIds.includes(user.id));
        } catch (error) {
        }
      }
      
      // Filtrar por termo de busca, se informado
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        result = result.filter(user => 
          (user.username && user.username.toLowerCase().includes(query)) || 
          (user.name && user.name.toLowerCase().includes(query)) || 
          (user.email && user.email.toLowerCase().includes(query))
        );
      }
      
      setFilteredUsers(result);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Não foi possível filtrar utilizadores: " + error.message,
        variant: "destructive",
      });
      setFilteredUsers(users); // Reverter para todos os utilizadores em caso de erro
    } finally {
      setLoading(false);
    }
  };

  const handleStoreChange = (storeId: string | null) => {
    setSelectedStore(storeId);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let parsedValue: any = value;
    
    if (name === "points") {
      parsedValue = value === "" ? 0 : parseInt(value, 10);
    }
    
    setFormData(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      avatar: "",
      role: "user",
      points: 0,
      status: "active",
      connectedAccount: "",
    });
    setIsAdding(false);
    setEditingId(null);
  };

  const startEditing = async (user: User) => {
    setFormData({
      name: user.username || user.name,
      email: user.email,
      avatar: user.avatar_url || user.avatar || "",
      role: user.role,
      points: user.points || 0,
      status: user.status,
      connectedAccount: user.connectedAccountId || "",
      mainStoreId: user.main_store_id
    });
    setEditingId(user.id);
    setIsAdding(true);
  };

  return {
    users,
    filteredUsers,
    isAdding,
    editingId,
    loading,
    stores,
    selectedStore,
    searchQuery,
    formData,
    activeTab,
    setIsAdding,
    setActiveTab,
    handleStoreChange,
    handleSearchChange,
    handleInputChange,
    handleSelectChange,
    resetForm,
    startEditing,
    fetchUsers,
  };
}
